'use client'
import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { initReplicache } from '../replicache/frontend/init'
import { teamSpaceMutators } from '@basehub/data/replicache/mutators/team-space/mutators'
import { AtLeast, Team, getTeamSpaceName } from '@basehub/data/replicache/index'
import { useDashboardSlugs } from '../hooks/use-dashboard-slugs'
import { useUserTeamsList } from './team/use-user-teams-list'
import { teamMutators } from '@basehub/data/replicache/mutators/team-space/team/mutators'
import { PublicUser } from '~/app/api/users/public-user-schema'
import { usePersonalTeamAndRepos } from './use-personal-team-and-repos'
import { useUserStore } from '~/app/_store/user-store'
import { dropDatabase } from 'replicache'

type TeamReplicacheContext = {
  data: TeamReplicacheQueryData | null | undefined
  isLoading: boolean
  isSuccess: boolean
  isFetching: boolean
  isError: boolean
}

const TeamReplicacheContext = React.createContext<
  TeamReplicacheContext | undefined
>(undefined)

export const TeamReplicacheProvider = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const query = useTeamReplicacheQuery()
  const memoizedQuery = React.useMemo(
    () => ({
      data: query.data,
      isSuccess: query.isSuccess,
      isLoading: query.isInitialLoading,
      isFetching: query.isFetching,
      isError: query.isError,
    }),
    [
      query.data,
      query.isSuccess,
      query.isInitialLoading,
      query.isFetching,
      query.isError,
    ]
  )

  return (
    <TeamReplicacheContext.Provider value={memoizedQuery}>
      {children}
    </TeamReplicacheContext.Provider>
  )
}

export const useTeamReplicache = () => {
  const context = React.useContext(TeamReplicacheContext)
  if (!context) {
    throw new Error(
      'useTeamReplicache must be used within a TeamReplicacheProvider'
    )
  }
  return context
}

type TeamReplicacheOptions = {
  forcePersonalTeam?: boolean
}

/**
 * Primitive hook (you shouldn't use this unless you have a very specific need).
 * Use the context consumer hook instead!
 */
export const useTeamReplicacheQuery = (options?: TeamReplicacheOptions) => {
  const repoSlug = useDashboardSlugs((s) => s.repoSlug)
  const { team: personalTeam } = usePersonalTeamAndRepos()
  const optimisticUser = useUserStore((s) => s.optimisticUser)
  const {
    data: { activeTeam },
  } = useUserTeamsList()

  const optionsRef = React.useRef(options)
  // eslint-disable-next-line react-compiler/react-compiler
  optionsRef.current = options

  let team: Team | undefined = undefined
  if (!activeTeam || options?.forcePersonalTeam) {
    if (options?.forcePersonalTeam) {
      team = personalTeam
    }
  } else {
    team = activeTeam
  }

  const queryKey = React.useMemo(
    () =>
      team?.id && optimisticUser?.id
        ? getTeamReplicacheQueryKey(
            { id: team?.id },
            { id: optimisticUser?.id }
          )
        : null,
    [optimisticUser, team?.id]
  )

  const query = useQuery(
    queryKey ?? [],
    async () => {
      if (!team) {
        throw new Error("shouldn't be enabled")
      }
      if (!optimisticUser?.id) {
        throw new Error("shouldn't be enabled")
      }

      const { replicache } = await initReplicache({
        spaceID: getTeamSpaceName(team.id),
        userId: optimisticUser.id,
        mutators: teamSpaceMutators,
        type: 'team',
        clientOnly: {
          team,
          repo: null,
          isGuest: false,
          isGuestInHisBranch: false,
          branchOrCommit: null,
        },
      })

      return {
        replicache,
        teamSlug: team.slug,
        teamId: team.id,
        team,
      }
    },
    {
      enabled: Boolean(queryKey),
      staleTime: Infinity,
      retry: 0,
      networkMode: 'always',
    }
  )

  type Data = typeof query.data

  const replicache = query.data?.replicache
  const queryClient = useQueryClient()

  React.useEffect(() => {
    if (!replicache || !team?.id || !queryKey) return

    const unsub = replicache.subscribe(
      (tx) => teamMutators.getTeam(tx, team.id),
      {
        onData: (team) => {
          queryClient.setQueryData<Data>(queryKey, (p) => {
            if (!p) return p
            if (!team) return p
            return { ...p, team, teamId: team.id, teamSlug: team.slug }
          })
        },
      }
    )

    return () => {
      unsub()
    }
  }, [queryClient, queryKey, replicache, team?.id])

  // refresh replicache w a query param
  React.useEffect(() => {
    if (!query.data?.replicache) return
    if (repoSlug) return
    const searchParams = new URLSearchParams(window.location.search)
    if (typeof searchParams.get('refresh-replicache') === 'string') {
      dropDatabase(query.data.replicache.idbName)
      searchParams.delete('refresh-replicache')
      let search = searchParams.toString()
      if (search) search = `?${search}`
      window.location.href =
        window.location.pathname + search + window.location.hash
    }
  }, [query.data?.replicache, repoSlug])

  return {
    ...query,
    /**
     * WARNING: `isLoading` will give true even when replicache query is disabled,
     * to know if the query is fetching AND loading use `isInitialLoading` instead.
     */
    isLoading: query.isLoading,
  }
}

export function getTeamReplicacheQueryKey(
  team: AtLeast<Team, 'id'> | undefined | null,
  user: AtLeast<PublicUser, 'id'> | undefined | null
) {
  return ['team-replicache', team?.id, user?.id] as const
}

export type TeamReplicacheQueryData = NonNullable<
  ReturnType<typeof useTeamReplicacheQuery>['data']
>
